import {
  PFlex,
  PFlexItem,
  PCheckboxWrapper,
  PHeadline,
  PTextFieldWrapper,
  PButtonPure,
  PText
} from '@porsche-design-system/components-react'
import React from 'react'
import { useForm, FormProvider } from 'react-hook-form'

import * as AppApi from '../../../utils/api/Apps'

import { ApplicationContext } from '../../../utils/Context/ApplicationContext'
import AppIDField from '../../AppIDField/AppIDField'
import DepartmentField from '../../DepartmentField/DepartmentField'
import IctoField from '../../IctoField/IctoField'
import InputWithButton from '../../InputWithButton/InputWithButton'
import DrawerFooter from '../Footer/DrawerFooter'

import { TOAST_TIME } from '../../../config/config'
import { PortalContext } from '../../../utils/PortalContext'

import PAGRating from '../../../Types'
import styles from './drawernewapp.module.css'

const DrawerNewApp = () => {
  const defaultValue = {
    icto: '',
    appId: '',
    appDescription: '',
    department: '',
    applicationManager: '',
    applicationManagerEmail: '',
    authorizedEmail: '',
    businessResponsible: false
  }

  const { setOpenDrawer, setLoading, setToast, setToastContent, setError } = React.useContext(PortalContext)

  const { applications, setApplications } = React.useContext(ApplicationContext)
  const [authorizedEmails, setAuthorizedEmails] = React.useState([defaultValue.authorizedEmail])

  const methods = useForm<PAGRating.FormAppInformation>({
    defaultValues: defaultValue,
    mode: 'onBlur'
  })

  const addNewApplication = (data: PAGRating.FormAppInformation) => {
    setLoading(true)
    data = {
      ...data,
      members: authorizedEmails
    }
    AppApi.create(data)
      .then((response) => response.json())
      .then((newApp) => {
        setApplications([...applications, newApp])
        setToastContent('Die App wurde erfolgreich angelegt!')
        setToast(true)
        methods.reset(defaultValue)
        setOpenDrawer(false)
        setTimeout(() => {
          setLoading(false)
          setToast(false)
        }, TOAST_TIME)
      })
      .catch((err) => {
        setError(true)
        setToastContent(err.message)
        setToast(true)
      })
  }

  const addEmail = (event: React.MouseEvent) => {
    event.preventDefault()
    if (methods.watch('authorizedEmail') !== undefined && methods.watch('authorizedEmail') !== '') {
      const newArr = [methods.watch('authorizedEmail')]
      const lowerCaseMails = newArr.map((element) => {
        return element.toLowerCase()
      })
      if (authorizedEmails[0] !== '') {
        methods.setValue('authorizedEmail', '', { shouldDirty: true })
        return setAuthorizedEmails([...authorizedEmails, ...lowerCaseMails])
      }
      methods.setValue('authorizedEmail', '', { shouldDirty: true })
      return setAuthorizedEmails([...lowerCaseMails])
    }
  }

  const deleteEmail = (email: string) => {
    return function () {
      const emailSelected = email
      const newArr = authorizedEmails.filter((email) => email !== emailSelected)
      return setAuthorizedEmails([...newArr])
    }
  }

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      addEmail(e)
    }
  }

  const cancelNewApplication = (event: React.MouseEvent) => {
    event.preventDefault()
    methods.reset(defaultValue)
    setOpenDrawer(false)
  }

  React.useEffect(() => {
    methods.register('appDescription', { required: true })
    methods.register('applicationManager', { required: true })
    methods.register('applicationManagerEmail', {
      required: true,
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Bitte E-Mail Format beachten.'
      }
    })
  }, [methods])

  return (
    <FormProvider {...methods}>
      <form className={styles.form} onSubmit={methods.handleSubmit(addNewApplication)} noValidate>
        <PFlex wrap='wrap' direction='column' justifyContent='space-between' className={styles.container}>
          <main className={styles.content}>
            <PHeadline variant='headline-5'>Identifikation</PHeadline>
            <PTextFieldWrapper
              label='Bezeichnung'
              className={styles.textfield}
              state={methods.errors.appDescription ? 'error' : 'none'}
              message='Das Feld wird benötigt.'
            >
              <input type='text' required name='appDescription' placeholder='Name' ref={methods.register} />
            </PTextFieldWrapper>
            <IctoField />
            <AppIDField />
            <PHeadline variant='headline-5' className={styles.subtitle}>
              IT-Ansprechpartner
            </PHeadline>
            <PTextFieldWrapper
              label='Application Manager (FD)'
              className={styles.textfield}
              state={methods.errors.applicationManager ? 'error' : 'none'}
              message='Das Feld wird benötigt.'
            >
              <input
                type='text'
                required
                name='applicationManager'
                placeholder='Vorname Nachname'
                ref={methods.register}
              />
            </PTextFieldWrapper>
            <DepartmentField />
            <PTextFieldWrapper
              label='E-Mail'
              className={styles.textfield}
              state={methods.errors.applicationManagerEmail ? 'error' : 'none'}
              message={
                methods.errors.applicationManagerEmail?.type === 'pattern'
                  ? methods.errors.applicationManagerEmail?.message
                  : 'Das Feld wird benötigt.'
              }
            >
              <input
                type='text'
                required
                name='applicationManagerEmail'
                placeholder='vorname.nachname@porsche.de'
                ref={methods.register}
              />
            </PTextFieldWrapper>
            <PCheckboxWrapper label='Vom Fachbereich verantwortet' className={styles.check}>
              <input type='checkbox' name='businessResponsible' ref={methods.register} />
            </PCheckboxWrapper>
            <PHeadline variant='headline-5'>Zugriffsberechtigt</PHeadline>
            <InputWithButton
              textFieldMethods={methods}
              buttonIcon='add'
              textFieldName='authorizedEmail'
              buttonFunction={addEmail}
              textFieldPlaceholder='Person via E-Mail hinzufügen'
              buttonDescription='addEmail'
              textFieldOnKeyPress={handleKeyPress}
              labelText='Hinzufügen'
            />
            <PFlex direction='column' className={styles.emailList}>
              {authorizedEmails[0] !== '' &&
                authorizedEmails.map((email, id) => (
                  <PFlexItem key={id} className={styles.email}>
                    <PText ellipsis>{email}</PText>
                    {email && (
                      <PButtonPure className={styles.closeIcon} icon='close' hideLabel onClick={deleteEmail(email)}>
                        deleteEmail
                      </PButtonPure>
                    )}
                  </PFlexItem>
                ))}
            </PFlex>
          </main>
          <DrawerFooter onClick={cancelNewApplication} />
        </PFlex>
      </form>
    </FormProvider>
  )
}

export default DrawerNewApp
